
import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Card } from "@/components/ui/card";
import { DraggableLineItem } from './DraggableLineItem';
import { LineItemWithMatchInfo } from '@/api/generated/client/models/LineItemWithMatchInfo';
import { cn } from "@/lib/utils";

interface UnmatchedLineItemsSectionProps {
    insurerItems: LineItemWithMatchInfo[];
    contractorItems: LineItemWithMatchInfo[];
    roomId: number;
    selectedItemIds?: Set<number>;
    onToggleItemSelection?: (itemId: number) => void;
    hasSelectedItemsInThisRoom: boolean; // Simplified: true if this room has the selection, false if another room does
}

export function UnmatchedLineItemsSection({ 
    insurerItems, 
    contractorItems, 
    roomId, 
    selectedItemIds, 
    onToggleItemSelection,
    hasSelectedItemsInThisRoom 
}: UnmatchedLineItemsSectionProps) {
    // Drop zone for "Unmatching" - i.e., dragging from a match group BACK to unmatched
    const { setNodeRef: setUnmatchedRef } = useDroppable({
        id: `unmatched-room-${roomId}`,
        data: { type: 'unmatched-zone', roomId }
    });

    return (
        <div ref={setUnmatchedRef} className="bg-slate-50/50 dark:bg-slate-900/20 rounded-lg border border-dashed border-slate-200 p-4 mt-6">
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-4">Unmatched Line Items</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Insurer Unmatched */}
                <Card className="p-3 bg-white dark:bg-slate-950 border-slate-200 h-full min-h-[150px]">
                    <div className="text-[10px] text-muted-foreground uppercase font-bold mb-2">Insurer Items</div>
                    <div className="space-y-2">
                        {insurerItems.map(item => (
                            <DroppableLineItem
                                key={item.lineItemId}
                                item={item}
                                side="insurer"
                                selectedItemIds={selectedItemIds}
                                onToggleItemSelection={onToggleItemSelection}
                                hasSelectedItemsInThisRoom={hasSelectedItemsInThisRoom}
                            />
                        ))}
                        {insurerItems.length === 0 && (
                            <div className="text-sm text-muted-foreground italic text-center py-8">
                                No unmatched insurer items
                            </div>
                        )}
                    </div>
                </Card>

                {/* Contractor Unmatched */}
                <Card className="p-3 bg-blue-50/10 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900 h-full min-h-[150px]">
                    <div className="text-[10px] text-blue-700/70 dark:text-blue-400 uppercase font-bold mb-2">Contractor Items</div>
                    <div className="space-y-2">
                        {contractorItems.map(item => (
                            <DroppableLineItem
                                key={item.lineItemId}
                                item={item}
                                side="contractor"
                                selectedItemIds={selectedItemIds}
                                onToggleItemSelection={onToggleItemSelection}
                                hasSelectedItemsInThisRoom={hasSelectedItemsInThisRoom}
                            />
                        ))}
                        {contractorItems.length === 0 && (
                            <div className="text-sm text-muted-foreground italic text-center py-8">
                                No unmatched contractor items
                            </div>
                        )}
                    </div>
                </Card>

            </div>
        </div>
    );
}


function DroppableLineItem({ 
    item, 
    side, 
    selectedItemIds, 
    onToggleItemSelection,
    hasSelectedItemsInThisRoom 
}: { 
    item: LineItemWithMatchInfo; 
    side: 'insurer' | 'contractor';
    selectedItemIds?: Set<number>;
    onToggleItemSelection?: (itemId: number) => void;
    hasSelectedItemsInThisRoom: boolean;
}) {
    const { setNodeRef, isOver } = useDroppable({
        id: `unmatched-item-${item.lineItemId}`,
        data: { type: 'unmatched-item', itemId: item.lineItemId, side }
    });

    const isSelected = selectedItemIds?.has(item.lineItemId!) || false;
    
    // Simple logic: if items are selected from another room, this item is disabled
    const isDisabled = !hasSelectedItemsInThisRoom;

    return (
        <div ref={setNodeRef} className={cn("rounded transition-colors", isOver && "bg-primary/20 ring-2 ring-primary")}>
            <DraggableLineItem
                item={{ ...item, type: side } as any}
                isSelected={isSelected}
                onToggle={() => onToggleItemSelection?.(item.lineItemId!)}
                isLocked={isDisabled}
                showCheckbox={true}
            />
        </div>
    );
}
